import React, { Fragment, useMemo } from 'react';
import { Stack, Typography, alpha, useTheme } from '@mui/material';
import type { Option, Screen } from '../../types';
import type { WorkflowStepMeta } from '../../config/workflowUi';
import { getStepHelpTooltip } from '../../config/wizardTooltips';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import WorkflowWizardStep from './WorkflowWizardStep';
import type { WorkflowStepState } from './WorkflowWizardStep';
import type { WorkflowChromePalette } from './WorkflowStepComponents';

export interface WorkflowWizardStepperProps {
  steps: WorkflowStepMeta[];
  activeIndex: number;
  navigationHistory: Screen[];
  selectedOption: Option;
  workflowSelected: boolean;
  compact: boolean;
  muteCompletedWorkflowSteps: boolean;
  currentStepLabel: string;
  onStepClick: (stepScreen: Screen, stepIndex: number) => void;
  blockNavigation: boolean;
  workflowPalette: WorkflowChromePalette;
}

/**
 * Where a step stands, read from progress alone. A step the run never reached is
 * locked, and stays locked while navigation is merely held, so a busy screen
 * never makes finished work look unreachable.
 */
const resolveStepState = (
  step: WorkflowStepMeta,
  index: number,
  activeIndex: number,
  navigationHistory: Screen[]
): WorkflowStepState => {
  if (index === activeIndex) {
    return 'active';
  }
  if (index < activeIndex) {
    return 'complete';
  }
  return navigationHistory.includes(step.screen) ? 'reached' : 'locked';
};

const WorkflowWizardStepper: React.FC<WorkflowWizardStepperProps> = ({
  steps,
  activeIndex,
  navigationHistory,
  selectedOption,
  workflowSelected,
  compact,
  muteCompletedWorkflowSteps,
  currentStepLabel,
  onStepClick,
  blockNavigation,
  workflowPalette: _workflowPalette,
}) => {
  const theme = useTheme();

  const menuIndex = useMemo(
    () => steps.findIndex((step) => step.screen === 'menu'),
    [steps]
  );

  if (steps.length === 0) {
    return null;
  }

  const isStepClickable = (index: number): boolean => {
    if (blockNavigation) {
      return false;
    }

    const screen = steps[index]?.screen;
    if (!screen || !navigationHistory.includes(screen)) {
      return false;
    }

    if (menuIndex >= 0 && index > menuIndex && !workflowSelected) {
      return false;
    }

    return true;
  };

  return (
    <Stack
      direction="row"
      component="nav"
      aria-label={`Guided setup progress. Current step: ${currentStepLabel}.`}
      flexWrap="nowrap"
      useFlexGap
      spacing={compact ? 0 : 0.85}
      alignItems="center"
      justifyContent="flex-start"
      sx={{
        minWidth: 0,
        flexShrink: 0,
        rowGap: 0,
        columnGap: compact ? { xs: 0.08, sm: 0.14, md: 0.22, lg: 0.28 } : 0.72,
        py: compact ? 0 : 0.18,
      }}
    >
      {steps.map((step, index) => {
        const state = resolveStepState(step, index, activeIndex, navigationHistory);
        const stepHelp = getStepHelpTooltip(step.screen, selectedOption);

        const stepControl = (
          <WorkflowWizardStep
            step={step}
            state={state}
            muted={muteCompletedWorkflowSteps && state === 'complete'}
            compact={compact}
            clickable={isStepClickable(index)}
            blocked={blockNavigation}
            onSelect={() => {
              onStepClick(step.screen, index);
            }}
          />
        );

        return (
          <Fragment key={step.screen}>
            {compact && stepHelp ? (
              <ActionHelpTooltip help={stepHelp} placement="bottom">
                <span style={{ display: 'inline-flex' }}>{stepControl}</span>
              </ActionHelpTooltip>
            ) : (
              stepControl
            )}
            {!compact && index < steps.length - 1 ? (
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.35),
                  lineHeight: 1,
                  flexShrink: 0,
                }}
                aria-hidden
              >
                →
              </Typography>
            ) : null}
          </Fragment>
        );
      })}
    </Stack>
  );
};

export default WorkflowWizardStepper;
